import React, { useRef, useState } from 'react';
import { UploadCloud, X, Loader2, ImageIcon, CheckCircle2 } from 'lucide-react';
import { adminV2Api } from '../../lib/api';
import { toast } from 'sonner';

/**
 * <ImageUploader> — single-image upload widget.
 *
 * Props:
 *  - value:      current URL (string) or null
 *  - onChange:   (newUrl: string|null) => void
 *  - label:      caption shown above the dropzone
 *  - hint:       smaller help text below the label
 *  - aspect:     CSS aspect-ratio string (e.g. "4 / 5"). Default "4 / 5".
 *  - testid:     prefix for data-testid (default "image-uploader")
 *
 * Backend: POST /api/v2/admin/uploads (multipart `file`) → { url, width, height, bytes, format }
 */
export default function ImageUploader({
  value,
  onChange,
  label = 'Image',
  hint = 'Recommended: 1080 × 1350 (4:5 ratio), under 300 KB, WebP preferred.',
  aspect = '4 / 5',
  testid = 'image-uploader',
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [lastMeta, setLastMeta] = useState(null);

  const triggerPick = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  const handleFiles = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setError(null);

    // Client-side pre-check (server is canonical, this just gives instant feedback)
    if (!/^image\//i.test(file.type)) {
      setError('Please upload an image file (JPG, PNG, WebP, HEIC).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('File too large. Max 8 MB before compression.');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      // Use the admin axios instance via adminApi.upload (we'll expose via adminV2Api in lib/api)
      const res = await adminV2Api.uploadEventImage(fd);
      const data = res.data || {};
      if (!data.url) throw new Error('Upload failed');
      onChange(data.url);
      setLastMeta(data);
      toast.success('Image uploaded');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
      setError(typeof msg === 'string' ? msg : 'Upload failed');
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
      // reset input so re-selecting same file works
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  const clear = () => {
    onChange(null);
    setLastMeta(null);
    setError(null);
  };

  // Build full URL for preview (server returns /api/uploads/... — append BACKEND_URL)
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const previewSrc = value && value.startsWith('/') ? `${BACKEND_URL}${value}` : value;

  return (
    <div className="img-uploader" data-testid={testid}>
      <label className="img-uploader-label">{label}</label>
      <p className="img-uploader-hint">{hint}</p>

      <div
        className={`img-uploader-zone ${value ? 'img-uploader-zone-filled' : ''}`}
        style={{ aspectRatio: aspect }}
        onClick={triggerPick}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') triggerPick(); }}
        data-testid={`${testid}-zone`}
      >
        {value ? (
          <>
            <img src={previewSrc} alt="preview" className="img-uploader-preview" data-testid={`${testid}-preview`} />
            <button
              type="button"
              className="img-uploader-remove"
              onClick={(e) => { e.stopPropagation(); clear(); }}
              data-testid={`${testid}-remove`}
              aria-label="Remove image"
            >
              <X size={14} />
            </button>
            {lastMeta && (
              <div className="img-uploader-meta" data-testid={`${testid}-meta`}>
                <CheckCircle2 size={11} className="text-emerald-400" />
                <span>{lastMeta.width}×{lastMeta.height} · {Math.round((lastMeta.bytes || 0) / 1024)} KB</span>
              </div>
            )}
          </>
        ) : uploading ? (
          <div className="img-uploader-state">
            <Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" />
            <p>Compressing & uploading…</p>
          </div>
        ) : (
          <div className="img-uploader-state">
            <UploadCloud size={28} className="text-[var(--accent-gold)] mb-1" />
            <p className="text-zinc-300 text-sm font-medium">Click or drop an image</p>
            <p className="text-[11px] text-zinc-500 mt-1">JPG, PNG, WebP, HEIC · ≤ 8 MB</p>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />

      {error && (
        <p className="img-uploader-error" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

ImageUploader.Placeholder = function ImageUploaderPlaceholder({ aspect = '4 / 5' }) {
  return (
    <div className="img-uploader-zone img-uploader-skeleton" style={{ aspectRatio: aspect }}>
      <ImageIcon size={28} className="text-zinc-700" />
    </div>
  );
};
